import { inject, Injectable } from '@angular/core';
import { BaseHttpService } from './base-http.service';
import { Observable, forkJoin, map, of } from 'rxjs';
import {
  Contact,
  ContactAddresses,
  ContactDetail,
  ContactsQuery,
  NewContact,
  UpdateCompany,
  UpdateContact,
  UpdateContactAddress,
} from '../interfaces/contacts';
import {
  ContactAddressesDto,
  ContactDetailDto,
  ContactLightDto,
  ContactTypeDto,
  UpdateIDelibeMandatairesRights,
} from '../interfaces/dtos/contacts';
import { ContactsMapper } from '@shared/utils';
import { AddressesService } from './addresses.service';

@Injectable({
  providedIn: 'root',
})
export class ContactsService extends BaseHttpService {
  private readonly addressesService = inject(AddressesService);

  protected endPoint: string = 'Contacts';

  private withPhoneNumberIfExists(
    addresses: UpdateContactAddress[],
    phoneNumber: string | undefined
  ): UpdateContactAddress[] {
    if (!phoneNumber) return addresses;

    return addresses.map((address: UpdateContactAddress) =>
      address.type === 'PRINCIPALE' ? { ...address, adresse: { ...address.adresse, telephone: phoneNumber } } : address
    );
  }

  private withEmailIfExists(
    addresses: UpdateContactAddress[],
    email: string | null | undefined
  ): UpdateContactAddress[] {
    if (!email) return addresses;

    return addresses.map((address: UpdateContactAddress) =>
      address.type === 'PRINCIPALE' ? { ...address, adresse: { ...address.adresse, mail: email } } : address
    );
  }

  public insertOneContact(dto: NewContact): Observable<unknown> {
    const copy = { ...dto };
    let addressesDto = this.withPhoneNumberIfExists(copy.listContactsAdresses as UpdateContactAddress[], dto.phoneNumber);
    addressesDto = this.withEmailIfExists(addressesDto, dto.email);
    copy.listContactsAdresses = addressesDto as NewContact['listContactsAdresses'];
    return this.http.post(`${this.buildURL()}`, copy);
  }

  public updateOneContact(contactId: number, dto: UpdateContact): Observable<unknown> {
    const copy: Partial<UpdateContact> = { ...dto };
    let addressesDto = this.withPhoneNumberIfExists(copy.listContactsAdresses ?? [], dto.phoneNumber);
    addressesDto = this.withEmailIfExists(addressesDto, dto.email);
    delete copy.listContactsAdresses;

    const dtos: { id: number | null; dto: UpdateContactAddress }[] = [];

    for (const address of addressesDto) {
      dtos.push({
        id: address.addressId,
        dto: address,
      });
    }

    return forkJoin({
      contact: this.http.put(this.buildURL({ idOrSubEndpoint: contactId }), copy),
      addresses: this.addressesService.updateContactAddresses({ contactId, addresses: dtos }),
    });
  }

  public updateOneCompany(companyId: number, dto: UpdateCompany): Observable<unknown> {
    return this.http.put(this.buildURL({ idOrSubEndpoint: companyId }), dto);
  }

  public fetchOneContact(contactId: number): Observable<ContactDetail> {
    return this.http
      .get<ContactDetailDto>(`${this.buildURL({ idOrSubEndpoint: contactId })}`)
      .pipe(map((res) => ContactsMapper.toContactDetail(res)));
  }

  public fetchContactAddresses(contactId: number): Observable<ContactAddresses> {
    return this.http
      .get<ContactAddressesDto>(`${this.buildURL({ idOrSubEndpoint: `${contactId}/contactAdresses` })}`)
      .pipe(map((res) => ContactsMapper.toContactAddresses(res)));
  }

  public fetchContacts(options: { source: 'iAdmin' | 'iDelibe'; query?: ContactsQuery }): Observable<Contact[]> {
    const { source, query } = options;

    const baseURL =
      source === 'iAdmin'
        ? `${this.environment.apiIContactBaseUrlAdmin}/api/v1`
        : `${this.environment.apiIContactBaseUrl}/v1`;

    this.setBaseURL(baseURL);

    return this.http
      .post<ContactLightDto[]>(`${this.buildURL({ idOrSubEndpoint: 'lowInformation' })}`, query ?? {})
      .pipe(
        map((res) => res.filter((contact) => contact.id)),
        map((res) =>
          res.map((contact) => ({
            id: contact.id,
            name: `${contact.nom ? contact.nom.toUpperCase() : ''} ${contact.prenom ?? ''}`,
            active: !contact.nePlusUtiliser,
            isCompany: !!contact?.typeSociete,
            lastname: contact.nom ? contact.nom.toUpperCase() : '',
            firstname: contact.prenom ?? '',
          }))
        )
      );
  }

  public fetchContactTypes(): Observable<ContactTypeDto[]> {
    return this.http.get<ContactTypeDto[]>(
      `${this.buildURL({ overrideEndPoint: 'Global/ContactTypes', apiScope: 'iAdmin' })}`
    );
  }

  public updateIDelibeMandatairesRights(contactId: number, accessEnabled: boolean, login: string): Observable<unknown> {
    const dto: UpdateIDelibeMandatairesRights = { accessEnabled, login };
    return this.http.put(this.buildURL({ idOrSubEndpoint: `${contactId}/UpdateiDelibeMandatairesRights` }), dto);
  }

  public deleteOneContact(contactId: number): Observable<unknown> {
    return this.http.delete(`${this.buildURL({ idOrSubEndpoint: contactId })}`);
  }
}
